// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ClipboardCheck, AlertTriangle, CheckCircle2, Save, Lock, FileWarning,
  RefreshCw, Calendar, FileDown, Landmark,
} from 'lucide-react';
import DiscrepancyModal from '../components/DiscrepancyModal';
import BankStatementImporter from '../components/BankStatementImporter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_NAMES_PT = {
  '01': 'Janeiro', '02': 'Fevereiro', '03': 'Março', '04': 'Abril',
  '05': 'Maio', '06': 'Junho', '07': 'Julho', '08': 'Agosto',
  '09': 'Setembro', '10': 'Outubro', '11': 'Novembro', '12': 'Dezembro',
};

const VALUE_FIELDS = [
  { key: 's30_saldo_inicial', label: 'S-30 (a) Saldo inicial' },
  { key: 's30_saldo_final', label: 'S-30 (i) Saldo final' },
  { key: 's26_saldo_mes_anterior', label: 'S-26 Saldo mês anterior' },
  { key: 's26_saldo_final_mes', label: 'S-26 Saldo final do mês' },
  { key: 's26_total_donativos', label: 'S-26 Total Donativos (deve zerar)' },
  { key: 's26_conta_total', label: 'S-26 Conta — Total' },
  { key: 'extrato_bancario_saldo', label: 'Extrato bancário — Saldo' },
];

function monthLabel(yyyymm) {
  const [y, m] = yyyymm.split('-');
  return `${MONTH_NAMES_PT[m]}/${y}`;
}

export default function AuditChecklistPage() {
  const { user, isAdmin } = useAuth();
  const [period, setPeriod] = useState(null);   // {period_key, period_label, months}
  const [periodKey, setPeriodKey] = useState(null);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);       // checklist doc
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [issues, setIssues] = useState([]);
  const [discrepancyTrigger, setDiscrepancyTrigger] = useState(null); // {month, rule_ref, type, title}
  const [bankImporterOpen, setBankImporterOpen] = useState(false);

  const isAdminUser = isAdmin();
  const readOnly = data?.status === 'concluido';

  // Resolve current period on mount
  useEffect(() => {
    axios.get(`${API}/audit-checklist/period`)
      .then(res => {
        setPeriod(res.data);
        setPeriodKey(res.data.period_key);
      })
      .catch(() => toast.error('Erro ao carregar trimestre atual'));
  }, []);

  // Load eligible auditors (admin or role=auditor) for the auditor selector — S-27 §60.
  useEffect(() => {
    if (!isAdminUser) return;
    axios.get(`${API}/audit-checklist/eligible-auditors`)
      .then(r => setUsers(r.data))
      .catch(() => {});
  }, [isAdminUser]);

  const [denied, setDenied] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit-checklist/${key}`);
      setData(res.data);
      setIssues([]);
    } catch (e) {
      if (e.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error(e.response?.data?.detail || 'Erro ao carregar checklist');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (periodKey) load(periodKey);
  }, [periodKey, load]);

  const updateLocal = (patch) => setData(prev => ({ ...prev, ...patch }));

  const toggleItem = (idx) => {
    if (readOnly) return;
    const items = data.items.map((it, i) => i === idx ? { ...it, checked: !it.checked } : it);
    updateLocal({ items });
  };

  const setNotes = (idx, notes) => {
    if (readOnly) return;
    const items = data.items.map((it, i) => i === idx ? { ...it, notes } : it);
    updateLocal({ items });
  };

  const setValueField = (month, key, value) => {
    if (readOnly) return;
    const numeric = value === '' ? null : Number(value);
    const next = { ...(data.values_per_month || {}) };
    next[month] = { ...(next[month] || {}), [key]: numeric };
    updateLocal({ values_per_month: next });
  };

  const save = async () => {
    if (!data) return false;
    setSaving(true);
    try {
      const payload = {
        auditor_user_id: data.auditor_user_id,
        auditor_name: data.auditor_name,
        items: data.items,
        values_per_month: data.values_per_month,
        observations: data.observations,
      };
      await axios.put(`${API}/audit-checklist/${data.period_key}`, payload);
      toast.success('Checklist salva');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const validate = async () => {
    setValidating(true);
    try {
      const ok = await save();
      if (!ok) return; // don't validate stale state
      const res = await axios.post(`${API}/audit-checklist/${data.period_key}/validate`);
      setIssues(res.data.issues);
      if (res.data.ok) toast.success('Sem divergências encontradas!');
      else toast.warning(`${res.data.issues.length} divergência(s) encontrada(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao validar');
    } finally {
      setValidating(false);
    }
  };

  const exportPdf = async () => {
    if (!data) return;
    try {
      const response = await axios.get(
        `${API}/audit-checklist/${data.period_key}/export-pdf`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auditoria_${data.period_key}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao exportar PDF');
    }
  };

  const finalize = async () => {
    if (!data.auditor_user_id) return toast.error('Selecione o auditor designado');
    if (data.auditor_user_id === user?.id) {
      return toast.error('Você não pode finalizar uma auditoria onde é o próprio auditor');
    }
    if (!window.confirm('Finalizar esta auditoria? Após finalizar, ela ficará somente leitura.')) return;
    try {
      await save();
      const res = await axios.post(`${API}/audit-checklist/${data.period_key}/finalize`);
      setData(res.data);
      toast.success('Auditoria finalizada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao finalizar');
    }
  };

  const reopen = async () => {
    if (!window.confirm('Reabrir esta auditoria finalizada? Ela voltará ao estado de rascunho e poderá ser editada novamente.')) return;
    try {
      const res = await axios.post(`${API}/audit-checklist/${data.period_key}/reopen`);
      setData(res.data);
      toast.success('Auditoria reaberta para edição');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao reabrir auditoria');
    }
  };

  const completionPct = data
    ? Math.round((data.items.filter(i => i.checked).length / data.items.length) * 100)
    : 0;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="audit-checklist-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground font-heading flex items-center gap-2">
              <ClipboardCheck className="w-7 h-7 text-emerald-600" />
              Auditoria Trimestral
            </h1>
            <p className="text-muted-foreground mt-1">
              Checklist conforme S-27 §60–62 (auditor designado pelo COC)
            </p>
          </div>

          {period && (
            <Select value={periodKey || ''} onValueChange={setPeriodKey}>
              <SelectTrigger className="w-full sm:w-64" data-testid="period-select">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Trimestre" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={period.period_key}>{period.period_label} (atual)</SelectItem>
                {generatePastPeriods(period).map(p => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {denied ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground" data-testid="checklist-denied">
            Você não tem permissão para acessar a auditoria trimestral. Fale com o administrador se precisar de acesso.
          </CardContent></Card>
        ) : loading || !data ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin" />
          </div>
        ) : (
          <>
            {/* Status banner */}
            <Card className={readOnly ? 'border-emerald-500/50' : 'border-amber-500/40'}>
              <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                {readOnly ? (
                  <>
                    <Lock className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                        Auditoria finalizada em {new Date(data.finalized_at).toLocaleString('pt-BR')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Auditor: {data.auditor_name || '—'}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={reopen}
                      data-testid="reopen-button"
                      className="ml-2 rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                    >
                      <RefreshCw className="w-4 h-4 mr-1.5" />
                      Reabrir auditoria
                    </Button>
                  </>
                ) : (
                  <>
                    <ClipboardCheck className="w-5 h-5 text-amber-600" />
                    <p className="text-sm font-medium">Rascunho em andamento</p>
                  </>
                )}
                <span className="flex-1" />
                <Badge variant="outline" data-testid="completion-badge">{completionPct}% concluído</Badge>
                <Badge variant="outline">{data.period_label}</Badge>
              </CardContent>
            </Card>

            {/* Auditor selector */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Auditor designado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isAdminUser ? (
                  <Select
                    value={data.auditor_user_id || ''}
                    onValueChange={(v) => {
                      const u = users.find(x => x.id === v);
                      updateLocal({ auditor_user_id: v, auditor_name: u?.name || '' });
                    }}
                    disabled={readOnly}
                  >
                    <SelectTrigger data-testid="auditor-select">
                      <SelectValue placeholder="Selecione o auditor (não pode ser o servo de contas nem secretário)" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {data.auditor_name ? `Auditor: ${data.auditor_name}` : 'Nenhum auditor designado'}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  S-27 §60: o auditor não deve ser o servo de contas nem o secretário,
                  e não deve ser a mesma pessoa em trimestres consecutivos.
                  {' '}Apenas usuários com papel <strong>Auditor</strong> (ou Admin) aparecem nesta lista.
                </p>
              </CardContent>
            </Card>

            {/* Items checklist */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Itens a verificar</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {data.items.map((item, idx) => (
                  <div
                    key={item.key}
                    className="flex items-start gap-3 p-3 rounded-xl border border-border hover:bg-accent/30 transition-colors"
                    data-testid={`checklist-item-${item.key}`}
                  >
                    <Checkbox
                      checked={item.checked}
                      onCheckedChange={() => toggleItem(idx)}
                      disabled={readOnly}
                      className="mt-1"
                      data-testid={`checkbox-${item.key}`}
                    />
                    <div className="flex-1 space-y-1.5">
                      <label className="text-sm font-medium cursor-pointer block">{item.label}</label>
                      <Input
                        placeholder="Observação (opcional)..."
                        value={item.notes || ''}
                        onChange={(e) => setNotes(idx, e.target.value)}
                        disabled={readOnly}
                        className="text-xs h-8"
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Monthly key values */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Valores-chave por mês (S-26 / S-30)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.months.map(month => (
                  <div key={month} className="border border-border rounded-xl p-4 space-y-3">
                    <h3 className="font-semibold text-sm">{monthLabel(month)}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {VALUE_FIELDS.map(field => {
                        const v = data.values_per_month?.[month]?.[field.key];
                        return (
                          <div key={field.key} className="space-y-1">
                            <label className="text-xs text-muted-foreground">{field.label}</label>
                            <Input
                              type="number"
                              step="0.01"
                              value={v === null || v === undefined ? '' : v}
                              onChange={(e) => setValueField(month, field.key, e.target.value)}
                              disabled={readOnly}
                              placeholder="0,00"
                              data-testid={`value-${month}-${field.key}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Observations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Observações do auditor</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  rows={4}
                  value={data.observations || ''}
                  onChange={(e) => updateLocal({ observations: e.target.value })}
                  disabled={readOnly}
                  placeholder="Anote pendências, recomendações ou divergências encontradas..."
                  data-testid="observations-textarea"
                />
              </CardContent>
            </Card>

            {/* Validation result */}
            {issues.length > 0 && (
              <Card className="border-red-500/40 bg-red-50/40 dark:bg-red-950/20" data-testid="issues-card">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-red-700 dark:text-red-400">
                    <FileWarning className="w-5 h-5" />
                    {issues.length} divergência(s) encontrada(s)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {issues.map((issue, i) => (
                    <div key={`${issue.month}-${issue.rule}-${i}`} className="flex gap-2 items-start text-sm" data-testid={`issue-${i}`}>
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <Badge variant="outline" className="mr-2 text-xs">{monthLabel(issue.month)}</Badge>
                        <Badge variant="outline" className="mr-2 text-xs">{issue.rule}</Badge>
                        {issue.message}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs"
                        onClick={() => setDiscrepancyTrigger({
                          month: issue.month,
                          rule_ref: issue.rule,
                          title: issue.message.slice(0, 80),
                          type: mapRuleToType(issue.rule),
                        })}
                        data-testid={`file-discrepancy-${i}`}
                      >
                        Registrar
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
            {issues.length === 0 && data.values_per_month && Object.keys(data.values_per_month).length > 0 && (
              <Card className="border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/20">
                <CardContent className="p-4 flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">Nenhuma divergência ao validar os valores informados.</span>
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 justify-end sticky bottom-4">
              <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-button">
                <FileDown className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => setBankImporterOpen(true)}
                data-testid="open-bank-importer-button"
              >
                <Landmark className="w-4 h-4 mr-2" />
                Importar extrato
              </Button>
              {!readOnly && (
                <>
                  <Button variant="outline" onClick={save} disabled={saving} data-testid="save-button">
                    <Save className="w-4 h-4 mr-2" />
                    Salvar rascunho
                  </Button>
                  <Button variant="outline" onClick={validate} disabled={validating} data-testid="validate-button">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Validar coerência
                  </Button>
                  {isAdminUser && (
                    <Button
                      onClick={finalize}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="finalize-button"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Finalizar auditoria
                    </Button>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <DiscrepancyModal
        open={!!discrepancyTrigger}
        onOpenChange={(o) => !o && setDiscrepancyTrigger(null)}
        defaultPeriodKey={data?.period_key}
        defaultMonth={discrepancyTrigger?.month}
        defaultRuleRef={discrepancyTrigger?.rule_ref}
        defaultType={discrepancyTrigger?.type}
      />

      <BankStatementImporter
        open={bankImporterOpen}
        onOpenChange={setBankImporterOpen}
        months={data?.months || []}
      />
    </Layout>
  );
}

function mapRuleToType(rule) {
  if (!rule) return 'outra';
  if (rule.includes('§55')) return 'extrato_vs_s26';
  if (rule.includes('§42') || rule.includes('§50')) return 's30_vs_s26';
  if (rule.includes('§47')) return 'saldo_carryover';
  if (rule.includes('§43')) return 'donativos_nao_zero';
  return 'outra';
}

// Build the last 5 trimester options STRICTLY in the past relative to current.
function generatePastPeriods(current) {
  const order = [
    { start: 3, label: 'Março a Maio' },
    { start: 6, label: 'Junho a Agosto' },
    { start: 9, label: 'Setembro a Novembro' },
    { start: 12, label: 'Dezembro a Fevereiro' },
  ];
  const [curYear, curMonth] = current.period_key.split('-').map(Number);
  // Sort key = year * 12 + start_month
  const curKey = curYear * 12 + curMonth;
  const out = [];
  let y = curYear;
  let idx = order.findIndex(o => o.start === curMonth);
  while (out.length < 5) {
    idx -= 1;
    if (idx < 0) { idx = order.length - 1; y -= 1; }
    const o = order[idx];
    if (y * 12 + o.start >= curKey) continue; // safety guard
    const key = `${y}-${String(o.start).padStart(2, '0')}`;
    const labelYear = o.start === 12 ? `${y}/${y + 1}` : `${y}`;
    out.push({ key, label: `${o.label} de ${labelYear}` });
  }
  return out;
}
